// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Casino Lobby (Main Floor)
// ================================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCasino } from '../context/CasinoShell';

const GAME_CATEGORIES = [
  {
    id: 'slots',
    title: 'SLOT MACHINES',
    icon: '🎰',
    description: 'Provably fair digital slots',
    path: '/slots',
    count: 3,
    color: '#DC2626',
    featured: true,
  },
  {
    id: 'tables',
    title: 'TABLE GAMES',
    icon: '🃏',
    description: 'Blackjack, Roulette, Video Poker, Craps',
    path: '/tables',
    count: 4,
    color: '#7C3AED',
  },
  {
    id: 'sports',
    title: 'SPORTSBOOK',
    icon: '🏆',
    description: 'Bet on NHL, NFL, NBA, Soccer, UFC',
    path: '/sports',
    count: 5,
    color: '#EAB308',
  },
];

const FEATURED_SLOTS = [
  {
    id: 'classic-forge',
    title: 'Classic Forge',
    icon: '🔥',
    description: '3-reel classic • 5 paylines • Up to 100x',
    rtp: '95%',
    volatility: 'Medium',
    path: '/slots/classic-forge',
    color: '#DC2626',
    tag: 'POPULAR',
  },
  {
    id: 'forge-rush',
    title: 'Forge Rush',
    icon: '⚡',
    description: '5-reel video slot • 20 paylines • Bonus rounds',
    rtp: '96%',
    volatility: 'High',
    path: '/slots/forge-rush',
    color: '#FFD700',
    tag: 'NEW',
  },
  {
    id: 'golden-hammer',
    title: 'Golden Hammer',
    icon: '🔨',
    description: '5-reel progressive • Jackpot • 20 paylines',
    rtp: '94%',
    volatility: 'Very High',
    path: '/slots/golden-hammer',
    color: '#00FF88',
    tag: 'JACKPOT',
  },
];

const QUICK_LINKS = [
  { icon: '💰', label: 'Wallet', path: '/vault' },
  { icon: '✅', label: 'Verify', path: '/vault/verify' },
  { icon: '🏆', label: 'Leaderboard', path: '/leaderboard' },
  { icon: '🎁', label: 'Rewards', path: '/rewards' },
];

export default function CasinoLobby() {
  const navigate = useNavigate();
  const { forgeBalance, sessionStats, getSessionDuration } = useCasino();

  return (
    <div style={styles.container}>
      {/* Welcome Banner */}
      <div style={styles.banner}>
        <div style={styles.bannerLeft}>
          <h1 style={styles.bannerTitle}>🔥 FORGE CASINO</h1>
          <p style={styles.bannerSub}>PROVABLY FAIR • BLOCKCHAIN VERIFIED • MATHEMATICALLY IMPOSSIBLE TO CHEAT</p>
        </div>
        <div style={styles.bannerRight}>
          <div style={styles.balanceCard}>
            <span style={styles.balanceLabel}>$FORGE BALANCE</span>
            <span style={styles.balanceValue}>${forgeBalance.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div style={styles.quickLinks}>
        {QUICK_LINKS.map(link => (
          <button key={link.path} style={styles.quickLink} onClick={() => navigate(link.path)}>
            <span style={{ fontSize: 20 }}>{link.icon}</span>
            <span style={styles.quickLinkLabel}>{link.label}</span>
          </button>
        ))}
      </div>

      {/* Session Stats */}
      <div style={styles.statsRow}>
        <div style={styles.stat}>
          <span style={styles.statLabel}>SESSION</span>
          <span style={styles.statValue}>{getSessionDuration()}m</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statLabel}>SPINS</span>
          <span style={styles.statValue}>{sessionStats.spinsThisSession}</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statLabel}>NET</span>
          <span style={{
            ...styles.statValue,
            color: sessionStats.netPosition >= 0 ? '#00FF88' : '#DC2626',
          }}>
            {sessionStats.netPosition >= 0 ? '+' : ''}{sessionStats.netPosition.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Featured Slots */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>🎰 FEATURED SLOTS</h2>
        <div style={styles.gameGrid}>
          {FEATURED_SLOTS.map(game => (
            <button
              key={game.id}
              style={styles.gameCard}
              onClick={() => navigate(game.path)}
            >
              {game.tag && <span style={{ ...styles.tag, background: game.color + '33', color: game.color }}>{game.tag}</span>}
              <span style={styles.gameIcon}>{game.icon}</span>
              <h3 style={{ ...styles.gameTitle, color: game.color }}>{game.title}</h3>
              <p style={styles.gameDesc}>{game.description}</p>
              <div style={styles.gameStats}>
                <span style={styles.gameStat}>RTP: {game.rtp}</span>
                <span style={styles.gameStat}>{game.volatility}</span>
              </div>
              <div style={styles.playBtn}>PLAY NOW</div>
            </button>
          ))}
        </div>
      </div>

      {/* Game Categories */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>GAME FLOOR</h2>
        <div style={styles.categoryGrid}>
          {GAME_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              style={{
                ...styles.categoryCard,
                borderColor: cat.color + '44',
                opacity: cat.comingSoon ? 0.5 : 1,
              }}
              onClick={() => !cat.comingSoon && navigate(cat.path)}
              disabled={cat.comingSoon}
            >
              <span style={{ fontSize: 36 }}>{cat.icon}</span>
              <h3 style={{ ...styles.categoryTitle, color: cat.color }}>{cat.title}</h3>
              <p style={styles.categoryDesc}>{cat.description}</p>
              {cat.comingSoon && <span style={styles.comingSoon}>COMING SOON</span>}
              {!cat.comingSoon && <span style={styles.gameCount}>{cat.count} games</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Provably Fair Banner */}
      <div style={styles.fairBanner}>
        <h3 style={styles.fairTitle}>🔒 PROVABLY FAIR GAMING</h3>
        <p style={styles.fairText}>
          Every spin uses SHA-256 cryptographic hashing. The result is committed BEFORE you spin
          and verified AFTER. The casino mathematically CANNOT change the outcome. Every result is
          on-chain, auditable by anyone, anytime.
        </p>
        <button style={styles.fairBtn} onClick={() => navigate('/vault/verify')}>
          VERIFY ANY SPIN →
        </button>
      </div>

      {/* Responsible Gaming Footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          🛡️ Gambling should be fun. Set limits. Play responsibly.
        </p>
        <button style={styles.footerLink} onClick={() => navigate('/responsible')}>
          Responsible Gaming →
        </button>
        <p style={styles.footerSmall}>
          Alberta Problem Gambling Helpline: 1-866-332-2322 | 18+ Only
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '16px',
    maxWidth: 800,
    margin: '0 auto',
    minHeight: '100vh',
    background: '#0a0a0a',
  },
  banner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    background: 'linear-gradient(135deg, #1a0000 0%, #0a0000 100%)',
    borderRadius: 16,
    border: '1px solid #DC262633',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 16,
  },
  bannerLeft: {},
  bannerTitle: {
    fontSize: 24,
    fontWeight: 900,
    color: '#DC2626',
    letterSpacing: 3,
    margin: 0,
    fontFamily: "'Courier New', monospace",
  },
  bannerSub: {
    fontSize: 9,
    color: '#00FF88',
    letterSpacing: 2,
    fontWeight: 700,
    marginTop: 4,
  },
  bannerRight: {},
  balanceCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
  },
  balanceLabel: {
    fontSize: 9,
    color: '#666',
    letterSpacing: 1,
    fontWeight: 700,
  },
  balanceValue: {
    fontSize: 28,
    fontWeight: 900,
    color: '#FFD700',
    textShadow: '0 0 12px #FFD70044',
    fontFamily: "'Courier New', monospace",
  },
  quickLinks: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  quickLink: {
    flex: 1,
    padding: '10px 8px',
    borderRadius: 10,
    border: '1px solid #222',
    background: '#111',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
  },
  quickLinkLabel: {
    fontSize: 9,
    color: '#888',
    fontWeight: 700,
    letterSpacing: 1,
  },
  statsRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
  },
  stat: {
    flex: 1,
    padding: '10px',
    borderRadius: 8,
    background: '#111',
    border: '1px solid #1a1a1a',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  statLabel: {
    fontSize: 8,
    color: '#555',
    fontWeight: 700,
    letterSpacing: 1,
  },
  statValue: {
    fontSize: 16,
    fontWeight: 900,
    color: '#fff',
    fontFamily: "'Courier New', monospace",
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    color: '#fff',
    letterSpacing: 2,
    marginBottom: 12,
  },
  gameGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220, 1fr))',
    gap: 12,
  },
  gameCard: {
    padding: '16px',
    borderRadius: 12,
    border: '1px solid #222',
    background: '#111',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',
  },
  tag: {
    position: 'absolute',
    top: 8,
    right: 8,
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 8,
    fontWeight: 900,
    letterSpacing: 1,
  },
  gameIcon: { fontSize: 40, display: 'block', marginBottom: 8 },
  gameTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 1,
    margin: '4px 0',
  },
  gameDesc: {
    fontSize: 10,
    color: '#888',
    lineHeight: 1.5,
    marginBottom: 8,
  },
  gameStats: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 10,
  },
  gameStat: {
    fontSize: 9,
    color: '#555',
    fontWeight: 700,
  },
  playBtn: {
    padding: '8px 16px',
    borderRadius: 6,
    background: 'linear-gradient(180deg, #DC2626 0%, #991B1B 100%)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 2,
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
  },
  categoryCard: {
    padding: '20px',
    borderRadius: 12,
    border: '1px solid',
    background: '#111',
    textAlign: 'center',
    cursor: 'pointer',
  },
  categoryTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 1,
    margin: '8px 0 4px',
  },
  categoryDesc: {
    fontSize: 11,
    color: '#888',
    marginBottom: 8,
  },
  comingSoon: {
    fontSize: 9,
    color: '#555',
    fontWeight: 700,
    letterSpacing: 1,
  },
  gameCount: {
    fontSize: 10,
    color: '#00FF88',
    fontWeight: 700,
  },
  fairBanner: {
    padding: '20px',
    background: '#0a1a0a',
    borderRadius: 12,
    border: '1px solid #00FF8833',
    textAlign: 'center',
    marginBottom: 20,
  },
  fairTitle: {
    fontSize: 14,
    color: '#00FF88',
    fontWeight: 900,
    letterSpacing: 2,
    marginBottom: 8,
  },
  fairText: {
    fontSize: 11,
    color: '#888',
    lineHeight: 1.6,
    marginBottom: 12,
  },
  fairBtn: {
    padding: '8px 20px',
    borderRadius: 6,
    border: '1px solid #00FF88',
    background: '#00FF8822',
    color: '#00FF88',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    cursor: 'pointer',
  },
  footer: {
    textAlign: 'center',
    padding: '20px 0',
    borderTop: '1px solid #1a1a1a',
  },
  footerText: {
    fontSize: 12,
    color: '#888',
    marginBottom: 8,
  },
  footerLink: {
    background: 'none',
    border: 'none',
    color: '#DC2626',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 8,
  },
  footerSmall: {
    fontSize: 9,
    color: '#444',
  },
};
